import { Fragment, memo } from 'react'

import { GameId, StoreOfferProduct, SupportedLanguage, isGemPackOffer } from '@common'
import cx from 'classnames'

import { useTranslatable } from '@/localization/useTranslatable'
import { useSnowplowProductCardViewImpression } from '@/utils/snowplow'

import { BrawlCardProduct, HyperchargeAsset } from './BrawlCardProduct'
import { CardOfferContainer } from './CardOfferContainer'
import { CardProduct } from './CardProduct'
import { useFormattedAmount } from './utils'
import { useLocale } from '../../../locale/useLocale'
import { brawlHyperChargeAsset, isBrawlerOrSkin } from '../../brawlstars/bundle/helpers'
import { isCosmetic } from '../../productFilters'
import { DisplayAsset } from '../assetRendering/DisplayAsset'
import { ProductsContainer } from '../structure/ProductsContainer'
import { StoreTypography } from '../typography/StoreTypography'

import styles from './cardOffer.module.scss'

type BundleProps = {
  children?: React.ReactNode
  game: GameId
  product: StoreOfferProduct
}

const shouldHideCardTitle = (product: StoreOfferProduct) => {
  if (product.contents.length > 1) {
    return false
  }

  const titleKey = Object.keys(product.title)[0] as SupportedLanguage
  const productTitle = product.title[titleKey]
  const contentTitle = product.contents[0].product.title[titleKey]

  if (!productTitle || !contentTitle) {
    return false
  }

  return productTitle.localeCompare(contentTitle) === 0
}

export const CardOffer = memo(({ game, product }: BundleProps) => {
  const productCardSize =
    product.contents.length > 6 ? 'size-bundle-xs' : product.contents.length > 2 ? 'size-bundle-sm' : 'size-bundle-lg'

  const brawl = game === 'brawlstars'
  const brawlSingleOffer = brawl && product.contents.length === 1

  const isCosmeticOffer = isCosmetic(product)
  const cosmeticOfferProductImage = isCosmeticOffer ? product.images[0] : undefined

  const flare = game !== 'clashroyale' && !isCosmeticOffer
  const cocSingleItem = game === 'clashofclans' && product.contents.length === 1 && isCosmeticOffer

  const snowplowViewImpressionRef = useSnowplowProductCardViewImpression<HTMLDivElement>(
    product,
    'CardOffer',
    'CardOffer',
  )

  const onlyOneItemWithinOffer = product.contents.length === 1

  const translatable = useTranslatable()
  let headingTitle = translatable(product.title, '')
  let cardTitle = ''
  if (onlyOneItemWithinOffer && headingTitle === '') {
    const productTitle = translatable(product.contents[0].product.title, '')
    cardTitle = ''
    headingTitle = productTitle
  }

  return (
    <CardOfferContainer
      offerType="offer"
      product={product}
      flare={flare}
      brawlSingleOffer={brawlSingleOffer}
      ref={snowplowViewImpressionRef}
    >
      {brawlSingleOffer ? (
        <BrawlSingleOfferProduct product={product} />
      ) : (
        <>
          <Heading title={headingTitle} isCosmetic={isCosmeticOffer} />
          <ProductsContainer narrow={product.contents.length === 3} game={game}>
            {product.contents.map((content, i) => (
              <Fragment key={i}>
                {brawl ? (
                  <BrawlCardProduct
                    key={content.product.id}
                    size={productCardSize}
                    game={game}
                    content={content}
                    title={cardTitle}
                    isHypercharge={brawlHyperChargeAsset(content.product.id)}
                    isBrawlerOrSkin={isBrawlerOrSkin(content)}
                    offerContent
                  />
                ) : (
                  <CardProduct
                    key={content.product.id}
                    offerType="offer"
                    size={productCardSize}
                    game={game}
                    content={content}
                    title={onlyOneItemWithinOffer ? cardTitle : translatable(content.product.title, 'title')}
                    offerContent
                    hiddenTitle={shouldHideCardTitle(product)}
                    cocSingleItem={cocSingleItem}
                    cosmeticOfferProductImage={cosmeticOfferProductImage}
                  />
                )}
              </Fragment>
            ))}
          </ProductsContainer>
        </>
      )}
    </CardOfferContainer>
  )
})

CardOffer.displayName = 'CardOffer'

const Heading = memo(({ title, isCosmetic = false }: { title: string; isCosmetic?: boolean }) => {
  const locale = useLocale()
  return (
    <div className={cx(styles.offerHeading, { [styles.cosmeticHeading]: isCosmetic })}>
      <StoreTypography tag="h3" style="outlined" textSize="md" className={cx(styles.title, styles[`locale-${locale}`])}>
        {title}
      </StoreTypography>
    </div>
  )
})

Heading.displayName = 'Heading'

const BrawlSingleOfferProduct = memo(({ product }: { product: StoreOfferProduct }) => {
  const translatable = useTranslatable()
  const productContents = product.contents.at(0)

  if (!productContents) {
    throw new Error('Product contents are missing')
  }

  const isHyperCharge = brawlHyperChargeAsset(productContents.product.id)

  const formattedAmount = useFormattedAmount(product.contents[0].amount)
  return (
    <div className={styles.brawlSingleOffer}>
      <div className={cx(styles.offerHeading)}>
        <StoreTypography tag="h3" style="outlined" textSize="md" className={styles.title}>
          {translatable(product.title, 'title')}
        </StoreTypography>
        {!isGemPackOffer(product) && (
          <StoreTypography tag="div" style="outlined" textSize="md" className={styles.amount}>
            {formattedAmount}
          </StoreTypography>
        )}
      </div>
      <div className={cx(styles.imageContainer, { [styles.hyperchargeSingleOffer]: isHyperCharge })}>
        {isHyperCharge ? (
          <HyperchargeAsset title={translatable(product.title)} assetId={productContents.product.id} />
        ) : (
          <DisplayAsset content={productContents} gameId="brawlstars" />
        )}
      </div>
    </div>
  )
})

BrawlSingleOfferProduct.displayName = 'BrawlSingleOfferProduct'
